import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { XCircle, CheckCircle2, Loader2, Calendar, Clock, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

interface BookingItem {
  id: number;
  slot_id: number;
  status: string;
  parent_name: string;
  student_name?: string;
  start_time: string;
  end_time: string;
  teacher_first_name: string;
  teacher_last_name: string;
  config_title: string;
}

export default function AppointmentConfirmBatch() {
  const { groupToken } = useParams<{ groupToken: string }>();
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  const [autoConfirmTriggered, setAutoConfirmTriggered] = useState(false);

  const { data: bookings, isLoading, error } = useQuery<BookingItem[]>(
    ['appointments', 'confirm-batch', groupToken],
    async () => {
      const response = await api.get(`/appointments/public/confirm-batch/${groupToken}`);
      const d = response.data;
      return Array.isArray(d.data) ? d.data : [d.data];
    },
    { enabled: !!groupToken, retry: 1 }
  );

  const confirmMutation = useMutation(
    async () => {
      const response = await api.post(`/appointments/public/confirm-batch/${groupToken}`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setConfirmed(true);
        toast.success(data?.message || 'Alle Termine bestätigt!');
        setTimeout(() => navigate('/appointments/all'), 3000);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler bei der Bestätigung');
      }
    }
  );

  const allConfirmed = !!bookings?.length && bookings.every((b) => b.status === 'confirmed');
  const hasPendingBookings = !!bookings?.some((b) => b.status === 'pending_confirmation');

  useEffect(() => {
    if (!groupToken || !bookings || bookings.length === 0) return;
    if (confirmed || allConfirmed) return;
    if (!hasPendingBookings) return;
    if (confirmMutation.isLoading || autoConfirmTriggered) return;

    setAutoConfirmTriggered(true);
    confirmMutation.mutate();
  }, [
    groupToken,
    bookings,
    confirmed,
    allConfirmed,
    hasPendingBookings,
    confirmMutation.isLoading,
    confirmMutation.mutate,
    autoConfirmTriggered
  ]);

  if (isLoading || !groupToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (error || !bookings || bookings.length === 0) {
    const errMsg = (error as any)?.response?.data?.error;
    const is410 = (error as any)?.response?.status === 410;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">
            {is410 ? 'Reservierung abgelaufen' : 'Keine Buchungen'}
          </h1>
          <p className="text-gray-600 mb-4">
            {errMsg || 'Diese Buchungen sind nicht mehr verfügbar oder wurden bereits bestätigt.'}
          </p>
          <button
            onClick={() => navigate('/appointments/all')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  if (confirmed || allConfirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md bg-white rounded-lg shadow-lg p-8">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">{bookings.length} Termine bestätigt!</h1>
          <p className="text-gray-600 mb-4">
            Sie erhalten in Kürze eine E-Mail mit allen Terminen und Stornierungs-Links (einzeln und alle).
          </p>
          <p className="text-sm text-gray-500">Sie werden zur Übersicht weitergeleitet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold mb-4">Alle Termine bestätigen</h1>
        <p className="text-gray-600 mb-4">
          Mit einem Klick bestätigen Sie alle {bookings.length} Termine. Bitte innerhalb von 10 Minuten bestätigen.
        </p>
        <div className="bg-gray-50 rounded p-4 mb-6 max-h-80 overflow-y-auto">
          {bookings.map((b, idx) => (
            <div key={b.id} className={idx > 0 ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="font-semibold">
                  {format(parseISO(b.start_time), 'dd.MM.yyyy HH:mm', { locale: de })} –{' '}
                  {format(parseISO(b.end_time), 'HH:mm', { locale: de })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <span>{b.teacher_first_name} {b.teacher_last_name}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-green-50 border border-green-200 rounded p-3 mb-6 text-sm text-green-800">
          {confirmMutation.isLoading
            ? 'Bestätigung läuft automatisch. Bitte warten...'
            : 'Die Bestätigung wird automatisch ausgelöst. Falls nötig, können Sie unten erneut bestätigen.'}
        </div>
        {confirmMutation.isLoading && (
          <div className="w-full bg-green-600 text-white px-6 py-3 rounded flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Wird bestätigt...
          </div>
        )}
        {confirmMutation.isError && (
          <button
            onClick={() => confirmMutation.mutate()}
            disabled={confirmMutation.isLoading}
            className="w-full bg-green-600 text-white px-6 py-3 rounded hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <CheckCircle2 className="w-5 h-5" />
            Erneut: Alle {bookings.length} Termine bestätigen
          </button>
        )}
      </div>
    </div>
  );
}
